import { createSelector } from 'reselect';
import { createPropsExtender } from '@mews/framework';
import { mapBindingToPropSelector } from '@mews-commander/utils';
import {
    isSubscriptionOverdue,
    getSubscription,
    getSubscriptionCode,
    isActivityReportEnabled,
    areCustomerStatisticsEnabled,
    isCheckoutReportEnabled,
    arePrivateMessageThreadsEnabled,
    isSpaceAllocationOptimizationEnabled,
    isRoomingListEnabled,
    isMultiEnterpriseNewReservationScreenEnabled,
    isGroupBillingEnabled,
    isSmartTipsEnabled,
    hasSubscriptionApplicationProviderMode,
    hasSubscriptionPortfolioMode,
    hasSubscriptionServiceProviderMode,
    isAvailabilityBlockBookingEngineLinksEnabled,
    isAvailabilityBlockAdvancedFeaturesEnabled,
    areCustomLinksEnabled,
    areCalendarEventsEnabled,
    isContractErrorBannerEnabled,
    isContractWarningBannerEnabled,
    isBillingTemplatesEnabled,
    isPaymentAndCancellationPoliciesExceptionsEnabled,
} from '../../ducks/subscriptions';
import { getCurrentEnterpriseSubscriptionId, hasCurrentEnterpriseServiceId } from './enterprise';

const currentSubscriptionIdPropSelector = createPropsExtender('subscriptionId', getCurrentEnterpriseSubscriptionId);

export const [
    getCurrentEnterpriseSubscription,
    isCurrentEnterpriseOverdue,
    getCurrentEnterpriseSubscriptionCode,
    isCurrentEnterpriseContractErrorBannerEnabled,
    isCurrentEnterpriseContractWarningBannerEnabled,
    isCurrentEnterpriseActivityReportEnabled,
    areCurrentEnterpriseCustomerStatisticsEnabled,
    isCurrentEnterpriseCheckoutReportEnabled,
    areCurrentEnterprisePrivateMessageThreadsEnabled,
    isCurrentEnterpriseSpaceAllocationOptimizationEnabled,
    isCurrentEnterpriseRoomingListEnabled,
    isCurrentEnterpriseMultiEnterpriseNewReservationEnabled,
    isCurrentEnterpriseGroupBillingEnabled,
    isCurrentEnterpriseSmartTipsEnabled,
    isCurrentEnterpriseAvailabilityBlockBookingEngineLinksEnabled,
    isCurrentEnterpriseAvailabilityBlockAdvancedFeaturesEnabled,
    areCurrentEnterpriseCustomLinksEnabled,
    areCurrentEnterpriseCalendarEventsEnabled,
    hasCurrentEnterpriseApplicationProviderMode,
    hasCurrentEnterprisePortfolioMode,
    hasCurrentEnterpriseServiceProviderMode,
    isCurrentEnterpriseBillingTemplatesEnabled,
    isCurrentEnterprisePaymentAndCancellationPoliciesExceptionsEnabled,
] = mapBindingToPropSelector(currentSubscriptionIdPropSelector)([
    getSubscription,
    isSubscriptionOverdue,
    getSubscriptionCode,
    isContractErrorBannerEnabled,
    isContractWarningBannerEnabled,
    isActivityReportEnabled,
    areCustomerStatisticsEnabled,
    isCheckoutReportEnabled,
    arePrivateMessageThreadsEnabled,
    isSpaceAllocationOptimizationEnabled,
    isRoomingListEnabled,
    isMultiEnterpriseNewReservationScreenEnabled,
    isGroupBillingEnabled,
    isSmartTipsEnabled,
    isAvailabilityBlockBookingEngineLinksEnabled,
    isAvailabilityBlockAdvancedFeaturesEnabled,
    areCustomLinksEnabled,
    areCalendarEventsEnabled,
    hasSubscriptionApplicationProviderMode,
    hasSubscriptionPortfolioMode,
    hasSubscriptionServiceProviderMode,
    isBillingTemplatesEnabled,
    isPaymentAndCancellationPoliciesExceptionsEnabled,
]);

export const isCurrentEnterpriseServiceProviderWithBookableService = createSelector(
    hasCurrentEnterpriseServiceProviderMode,
    hasCurrentEnterpriseServiceId,
    (hasServiceProviderMode, hasServiceId) => hasServiceProviderMode && hasServiceId
);

export const isCurrentEnterpriseApplicationProvider = createSelector(
    hasCurrentEnterpriseServiceProviderMode,
    hasCurrentEnterpriseApplicationProviderMode,
    (hasServiceProviderMode, hasApplicationProviderMode) => !hasServiceProviderMode && hasApplicationProviderMode
);

export const isCurrentEnterprisePortfolio = createSelector(
    hasCurrentEnterpriseServiceProviderMode,
    hasCurrentEnterprisePortfolioMode,
    (hasServiceProviderMode, hasPortfolioMode) => !hasServiceProviderMode && !!hasPortfolioMode
);
